import { Capacitor, registerPlugin } from "@capacitor/core";

// Native counterpart lives in android/app/src/main/java/com/trendingz/app/PipPlugin.java
const Pip = registerPlugin("Pip");

/**
 * Native shell wiring: status bar, hardware back button, safe areas.
 *
 * Every call is guarded and dynamically imported, so nothing here loads or runs in
 * the web build - the plugins are simply absent there.
 */
export const initNativeShell = async () => {
  if (!Capacitor.isNativePlatform()) return;

  document.documentElement.classList.add("tz-native");

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    // The app is dark end to end; a light bar would sit oddly above it.
    await StatusBar.setBackgroundColor({ color: "#050505" });
  } catch {
    /* status bar is not available on every platform - not fatal */
  }

  try {
    const { App } = await import("@capacitor/app");

    // Android's back button otherwise closes the app from any screen, including
    // three levels into a video. Walk the history first and only exit at the root.
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack || window.history.length > 1) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });
  } catch {
    /* app plugin unavailable */
  }
};

/**
 * Tell the native shell whether leaving the app should shrink into a
 * Picture-in-Picture window.
 *
 * Set while a video is open, cleared when it closes. Without this the app would
 * drop into a PiP window every time someone pressed Home, playing or not.
 *
 * Note this is PiP, not background audio: YouTube's terms require the player stay
 * visible, so the video keeps showing in a small window rather than continuing as
 * sound alone.
 */
export const setPipEligible = async (value) => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Pip.setEligible({ value: Boolean(value) });
  } catch {
    /* plugin missing on this platform - PiP simply will not engage */
  }
};
